<template>
  <transition name="modal-animation">
    <div v-if="modalActive" class="modal" @keyup.esc="close">
      <div class="modal-inner">
        <button class="close" aria-label="Close" @click="close">
          <img class="w-15 h-15" :src="`${$assetsCdn}/icons/close-modal.svg`" alt="" />
        </button>
        <template v-if="!previewStatus">
          <Carousel :wrap-around="true" :slideIndexInit='slideIndex' :key="carousel">
            <template #counter >
              <SlideNumber class="carousel__number" v-if="modalImages.length > 1"/>
            </template>
            <Slide
              :index="index"
              v-for="(item, index) in modalImages"
              :key="index"
            >
              <div class="carousel__item flex justify-center align-center flex-column">
                <BaseResponsiveImages :src="item" :alt="item.caption || undefined" />
                <div class="mt-20 delete flex h-40 lh-40 w-135 bg-white br-8 justify-center items-center hand" :class="[ $device.isMobile ? 'absolute bottom-20 z-999' : '']" @click="deletePhoto(index)" v-if="shouldDelete">
                  <p class="mb-0 fw-700 lh-16">Delete Photo</p>
                </div>
                <div v-if="showText && item.caption" class="pt-16 white maxw-600 mb-maxw-300">
                  <p v-html="item.caption" class="text-md-rg"></p>
                </div>
              </div>
            </Slide>
            <template #addons>
              <Navigation v-if="!isMobile && modalImages.length > 1" />
            </template>
          </Carousel>
        </template>
        <template v-else>
          <div class="preview-wrap">
            <div class="preview-item">
              <img :src="previewImage.url" alt="" />
            </div>
          </div>
        </template>
      </div>
      <div class="modal-overlay" />
    </div>
  </transition>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import Slide from '~/components/ui/carousel/Slide'
import Carousel from '~/components/ui/carousel/Carousel'
import Navigation from '~/components/ui/carousel/Navigation'
import SlideNumber from '~/components/ui/carousel/SlideNumber'
import BaseResponsiveImages from '~/components/ui/BaseResponsiveImages/BaseResponsiveImages.vue'
import { PHONE } from '~/constant/common'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

export default {
  name: 'ModalBanner',
  props: {
    previewStatus: {
      type: Boolean,
      default: false,
    },
    previewImage: {
      type: Object,
      default: () => {},
    },
    modalActive: {
      required: true,
      type: Boolean,
    },
    modalImages: {
      required: true,
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    overflowBody: {
      type: Boolean,
      default: true,
    },
    slideIndex: {
      style: Number,
      default: 0
    },
    shouldDelete: {
      style: Boolean,
      default: false
    },
    showText: {
      style: Boolean,
      default: false
    }
  },
  components: {
    Slide,
    Carousel,
    Navigation,
    SlideNumber,
    BaseResponsiveImages
},
  setup(props, { slots, emit }) {
    const carousel = ref(0)

    const {isMobile} = useDevice()
    const generalStore = useGeneralInfoStore()

    const close = () => {
      emit('update:close', false)
    }
    const esc = (e) => {
      if (e.which === 27) {
        close()
      }
    }

    const windowWidth = computed(() => generalStore.generalData.windowWidth)

    const deletePhoto = async (index) => {
      if(index === props.modalImages.length - 1) {
        emit('updateSlideIndex', index - 1)
      }
      else emit('updateSlideIndex', index)
      emit('delete', index)
      carousel.value += 1
      await nextTick()
      if(props.modalImages.length === 0) {
        close()
      }
    }

    const hasFooterSlot = computed(() => !!slots.footer)

    watch(
      () => props.modalActive,
      (value) => {
        if (value) {
          if (props.overflowBody) {
            document.body.style.overflow = 'hidden'
          }
          window.addEventListener('keydown', esc)
        } else {
          if (props.overflowBody) {
            document.body.style.overflow = ''
          }
          window.removeEventListener('keydown', esc)
        }
      },
    )

    onMounted(() => {
      window.addEventListener('keydown', esc)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', esc)
    })
    return { hasFooterSlot, close, isMobile, deletePhoto, carousel, windowWidth, PHONE }
  },
}
</script>

<style lang="scss" scoped>
.modal-animation-enter-active {
  transition: all 0.25s ease;
}
.modal-animation-leave-active {
  transition: all 0.15s ease;
}
.modal-animation-enter-from,
.modal-animation-leave-to {
  opacity: 0;
  .modal-inner {
    transform: scale(0.8);
  }
}
.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  max-height: 100vh;
  width: 100%;
  max-width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99000;
  overflow: hidden;
  &.blur {
    backdrop-filter: saturate(180%) blur(15px);
  }
  .modal-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.8);
  }
  .modal-inner {
    position: relative;
    z-index: 99001;
    width: 100%;
    height: 100%;
    transition: all 0.15s ease;
    .close {
      position: absolute;
      right: 75px;
      top: 40px;
      width: 25px;
      height: 25px;
      z-index: 99001;

      @include phone {
        right: 19px;
      }
    }
    :deep(section.carousel) {
      height: 100%;
      .carousel__viewport {
        position: relative;
        height: 100%;
        .carousel__track {
          height: 100%;
        }
      }
      .carousel__prev {
        left: 80px;
      }
      .carousel__next {
        right: 80px;
      }
      @include phone {
        .carousel__prev {
          left: 10px;
        }
        .carousel__next {
          right: 10px;
        }
      }
    }
  }
}
.carousel__number {
  position: absolute;
  top: 40px;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);
}
.carousel__item {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  :deep(img) {
    width: auto;
    max-width: 1000px;
    height: auto;
    max-height: 600px;
    border-radius: 14px;
  }
  @include tablet {
    :deep(img) {
      max-width: 100%;
      height: auto;
      max-height: unset;
      border-radius: 14px;
      padding: 10px;
    }
  }
}
.preview-wrap{
  flex-shrink: 0;
  margin: 0;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>
